import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { APP_DOWNLOAD_URL, APP_DOWNLOAD_NAME } from "../lib/app.js";
import { useConfirm } from "../components/ConfirmDialog.js";

interface Device {
  id: string;
  hostname: string;
  os: string;
  access_code: string;
  last_seen_at: string | null;
  created_at: string;
  is_favorite: boolean;
  label: string | null;
  group_name: string | null;
  online: boolean;
}

const fmtId = (c: string) => c.replace(/(\d{4})(?=\d)/g, "$1 ");

function osIcon(os: string) {
  const o = os.toLowerCase();
  if (o.includes("win")) return "🪟";
  if (o.includes("mac")) return "🍎";
  if (o.includes("linux")) return "🐧";
  if (o.includes("android")) return "🤖";
  return "💻";
}

export function DevicesPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const confirm = useConfirm();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Add-device modal state.
  const [showAdd, setShowAdd] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [addPw, setAddPw] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const backdropDown = useRef(false);

  // Rename modal state (replaces the two native prompt() dialogs).
  const [renameDev, setRenameDev] = useState<Device | null>(null);
  const [renameLabel, setRenameLabel] = useState("");
  const [renameGroup, setRenameGroup] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameErr, setRenameErr] = useState<string | null>(null);

  // Card vs. compact list view (remembered across visits).
  const [view, setView] = useState<"grid" | "list">(
    () => (localStorage.getItem("devicesView") === "list" ? "list" : "grid")
  );
  useEffect(() => {
    localStorage.setItem("devicesView", view);
  }, [view]);

  async function refresh() {
    try {
      setDevices(await api<Device[]>("/v1/devices"));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : String(err));
    }
  }

  useEffect(() => {
    refresh();
    const h = setInterval(refresh, 5000);
    return () => clearInterval(h);
  }, []);

  async function toggleFavorite(d: Device) {
    try {
      if (d.is_favorite) {
        await api(`/v1/devices/${d.id}/favorite`, { method: "DELETE" });
      } else {
        await api(`/v1/devices/${d.id}/favorite`, { method: "PUT", body: "{}" });
      }
      refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : String(err));
    }
  }

  function rename(d: Device) {
    setRenameDev(d);
    setRenameLabel(d.label || d.hostname);
    setRenameGroup(d.group_name || "");
    setRenameErr(null);
  }

  async function saveRename() {
    const d = renameDev;
    if (!d) return;
    setRenameBusy(true);
    setRenameErr(null);
    try {
      await api(`/v1/devices/${d.id}/favorite`, {
        method: "PUT",
        body: JSON.stringify({
          label: renameLabel.trim() || d.hostname,
          groupName: renameGroup.trim() || null
        })
      });
      setRenameDev(null);
      await refresh();
    } catch (err) {
      setRenameErr(err instanceof ApiError ? err.code : String(err));
    } finally {
      setRenameBusy(false);
    }
  }

  async function addDevice() {
    const digits = addCode.replace(/\D/g, "");
    if (digits.length !== 8 || !addPw) return;
    setAddBusy(true);
    setAddErr(null);
    try {
      await api("/v1/connect/link", {
        method: "POST",
        body: JSON.stringify({ code: digits, password: addPw })
      });
      setShowAdd(false);
      setAddCode("");
      setAddPw("");
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        setAddErr(
          err.code === "invalid_code_or_password"
            ? "That ID or password isn't right. Check the machine's SPIDER Viewer screen."
            : err.code === "too_many_attempts"
              ? "Too many attempts for this ID — wait a few minutes and retry."
              : err.code
        );
      } else {
        setAddErr(String(err));
      }
    } finally {
      setAddBusy(false);
    }
  }

  async function removeDevice(d: Device) {
    // Admins/owners see the whole fleet by role, so unlinking their own claim
    // wouldn't hide the machine — for them Remove fully deletes the device.
    // Regular users just unlink it from their own account.
    const isAdmin = !!me?.isAdmin;
    const name = d.label || d.hostname;
    const ok = await confirm(
      isAdmin
        ? {
            title: `Delete "${name}" from the fleet?`,
            message:
              `This removes the machine for ALL users and ends any live session. ` +
              `If its app is still running it may re-register later.`,
            confirmLabel: "Delete",
            danger: true
          }
        : {
            title: `Remove "${name}" from your devices?`,
            message:
              `This only unlinks it from your account — the machine and its app are unaffected. ` +
              `You can add it back anytime with its ID and password.`,
            confirmLabel: "Remove"
          }
    );
    if (!ok) return;
    try {
      await api(isAdmin ? `/v1/devices/${d.id}` : `/v1/devices/${d.id}/access`, {
        method: "DELETE"
      });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : String(err));
    }
  }

  const connect = (d: Device) => navigate(`/connect?id=${encodeURIComponent(d.access_code)}`);

  // Compact single-line row for the list view.
  const row = (d: Device) => (
    <div
      key={d.id}
      className="glass flex items-center gap-3 px-4 py-2.5 border-l-4 border-l-blue-500/70 hover:border-l-blue-400 transition"
    >
      <div className="text-2xl shrink-0">{osIcon(d.os)}</div>
      <button
        type="button"
        onClick={() => toggleFavorite(d)}
        title={d.is_favorite ? "Remove from favorites" : "Add to favorites"}
        aria-label="Toggle favorite"
        className={`text-lg leading-none shrink-0 transition ${
          d.is_favorite ? "text-yellow-400 hover:text-yellow-300" : "text-white/25 hover:text-white/60"
        }`}
      >
        {d.is_favorite ? "★" : "☆"}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-white truncate">{d.label || d.hostname}</span>
          <span className={`shrink-0 w-2 h-2 rounded-full ${d.online ? "bg-green-400" : "bg-white/20"}`} />
        </div>
        <p className="text-[11px] text-white/45 font-mono truncate">
          ID {fmtId(d.access_code)}
          {d.label ? ` · ${d.hostname}` : ""}
          {d.group_name ? ` · ${d.group_name}` : ""}
        </p>
      </div>
      <button
        type="button"
        disabled={!d.online}
        onClick={() => connect(d)}
        className={`shrink-0 px-4 py-1.5 rounded-lg text-sm font-medium transition inline-flex items-center gap-1.5 ${
          d.online ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-white/10 text-white/40 cursor-not-allowed"
        }`}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
        Connect
      </button>
      <button
        type="button"
        onClick={() => rename(d)}
        className="shrink-0 p-1.5 rounded-lg text-white/50 hover:bg-white/10 transition"
        title="Rename / group"
        aria-label="Rename"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
      </button>
      <button
        type="button"
        onClick={() => removeDevice(d)}
        className="shrink-0 p-1.5 rounded-lg text-white/35 hover:bg-red-500/15 hover:text-red-300 transition"
        title="Remove"
        aria-label="Remove device"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M9 3h6l1 2h4v2H4V5h4l1-2zM6 9h12l-1 11a2 2 0 01-2 2H9a2 2 0 01-2-2L6 9z" /></svg>
      </button>
    </div>
  );

  const renderDevices = (list: Device[]) =>
    view === "list" ? (
      <div className="space-y-2">{list.map(row)}</div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{list.map(card)}</div>
    );

  const card = (d: Device) => (
    <div
      key={d.id}
      className="glass p-5 border-l-4 border-l-blue-500/70 hover:border-l-blue-400 transition group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-3xl">{osIcon(d.os)}</div>
          <div className="min-w-0">
            <h3 className="font-semibold text-white truncate">{d.label || d.hostname}</h3>
            <p className="text-xs text-white/50 font-mono">
              ID {fmtId(d.access_code)}
              {d.label ? ` · ${d.hostname}` : ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => toggleFavorite(d)}
          title={d.is_favorite ? "Remove from favorites" : "Add to favorites"}
          aria-label="Toggle favorite"
          className={`text-xl leading-none transition ${
            d.is_favorite ? "text-yellow-400 hover:text-yellow-300" : "text-white/25 hover:text-white/60"
          }`}
        >
          {d.is_favorite ? "★" : "☆"}
        </button>
      </div>

      <div className="flex items-center justify-between text-xs mb-4">
        <span className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${d.online ? "bg-green-400 animate-pulse" : "bg-white/20"}`} />
          <span className={d.online ? "text-green-400 font-medium" : "text-white/50"}>
            {d.online ? "Online" : "Offline"}
          </span>
        </span>
        {d.last_seen_at && !d.online && (
          <span className="text-white/40">seen {new Date(d.last_seen_at).toLocaleDateString()}</span>
        )}
      </div>

      <div className="flex gap-2 pt-3 border-t border-white/10">
        <button
          type="button"
          disabled={!d.online}
          onClick={() => connect(d)}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${
            d.online ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-white/10 text-white/40 cursor-not-allowed"
          }`}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Connect
        </button>
        <button
          type="button"
          onClick={() => rename(d)}
          className="px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-white/10 transition"
          title="Rename / group (adds to favorites)"
          aria-label="Rename"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => removeDevice(d)}
          className="px-3 py-2 rounded-lg text-sm text-white/40 hover:bg-red-500/15 hover:text-red-300 transition"
          title="Remove from my devices"
          aria-label="Remove device"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 3h6l1 2h4v2H4V5h4l1-2zM6 9h12l-1 11a2 2 0 01-2 2H9a2 2 0 01-2-2L6 9z" />
          </svg>
        </button>
      </div>
    </div>
  );

  const favorites = (devices ?? []).filter((d) => d.is_favorite);
  const others = (devices ?? []).filter((d) => !d.is_favorite);

  // Group favorites by their user group name.
  const favGroups = Array.from(
    favorites.reduce((m, d) => {
      const g = d.group_name?.trim() ? d.group_name : "Ungrouped";
      (m.get(g) ?? m.set(g, []).get(g)!).push(d);
      return m;
    }, new Map<string, Device[]>())
  );

  return (
    <section className="space-y-8 pb-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Devices</h1>
          <p className="text-white/50">
            Machines linked to your account. Star the ones you use to pin them up top with your own
            names — then Connect with the machine's password.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-3">
          {/* Grid / list view toggle */}
          <div className="flex items-center rounded-lg bg-white/5 border border-white/10 p-0.5">
            <button
              type="button"
              onClick={() => setView("grid")}
              title="Card view"
              aria-label="Card view"
              className={`p-1.5 rounded-md transition ${view === "grid" ? "bg-white/15 text-white" : "text-white/50 hover:text-white"}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              title="List view"
              aria-label="List view"
              className={`p-1.5 rounded-md transition ${view === "list" ? "bg-white/15 text-white" : "text-white/50 hover:text-white"}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-2 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
            </svg>
            Add device
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 text-sm">
          {error === "unauthorized" ? "Your session expired — please sign in again." : error}
        </div>
      )}

      {devices === null ? (
        <div className="py-12 text-center text-white/50">Loading devices…</div>
      ) : devices.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">🖥️</div>
          <h3 className="text-xl font-semibold text-white mb-2">No devices yet</h3>
          <p className="text-white/50 max-w-md mx-auto mb-6">
            Install SPIDER Viewer on a machine, then add it here with its ID and password — or just
            connect to it once and it appears automatically.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
            >
              Add a device
            </button>
            <a
              href={APP_DOWNLOAD_URL}
              download={APP_DOWNLOAD_NAME}
              className="px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm font-medium transition"
            >
              Download the app
            </a>
          </div>
        </div>
      ) : (
        <div className="space-y-10">
          {favorites.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-sm font-semibold text-yellow-400/90 uppercase tracking-wider flex items-center gap-2">
                ★ Favorites <span className="text-white/30">({favorites.length})</span>
              </h2>
              {favGroups.map(([group, list]) => (
                <div key={group}>
                  {favGroups.length > 1 && (
                    <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">{group}</h3>
                  )}
                  {renderDevices(list)}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
              All machines <span className="text-white/30">({others.length})</span>
            </h2>
            {others.length === 0 ? (
              <p className="text-white/40 text-sm">All your machines are in your favorites.</p>
            ) : (
              renderDevices(others)
            )}
          </div>
        </div>
      )}

      {showAdd && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onMouseDown={(e) => {
            backdropDown.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && backdropDown.current) setShowAdd(false);
          }}
        >
          <div className="glass w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-white">Add a device</h2>
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="px-2 py-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-white/50 mb-4">
              Enter the ID and password shown on that machine's SPIDER Viewer app. It links to your
              account so you can reconnect anytime.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5 font-semibold">
                  Device ID
                </label>
                <input
                  inputMode="numeric"
                  autoFocus
                  placeholder="0000 0000"
                  value={addCode.replace(/\D/g, "").slice(0, 8).replace(/(\d{4})(?=\d)/g, "$1 ")}
                  onChange={(e) => setAddCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addDevice();
                  }}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-lg font-mono tracking-widest text-center focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5 font-semibold">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={addPw}
                  onChange={(e) => setAddPw(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addDevice();
                  }}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white font-mono tracking-widest text-center focus:border-blue-500 focus:outline-none"
                />
              </div>

              {addErr && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-red-300 text-sm">
                  {addErr}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="flex-1 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={addCode.replace(/\D/g, "").length !== 8 || !addPw || addBusy}
                  onClick={addDevice}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition"
                >
                  {addBusy ? "Adding…" : "Add device"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renameDev && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onMouseDown={(e) => {
            backdropDown.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && backdropDown.current) setRenameDev(null);
          }}
        >
          <div className="glass w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-white">Rename device</h2>
              <button
                type="button"
                onClick={() => setRenameDev(null)}
                className="px-2 py-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-white/50 mb-4">
              Give this machine a friendly name and group. These are just for your own list.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5 font-semibold">
                  Name
                </label>
                <input
                  autoFocus
                  placeholder={renameDev.hostname}
                  value={renameLabel}
                  onChange={(e) => setRenameLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename();
                  }}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5 font-semibold">
                  Group
                </label>
                <input
                  placeholder="Leave blank for Ungrouped"
                  value={renameGroup}
                  onChange={(e) => setRenameGroup(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename();
                  }}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              {renameErr && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-red-300 text-sm">
                  {renameErr}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setRenameDev(null)}
                  className="flex-1 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={renameBusy}
                  onClick={saveRename}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition"
                >
                  {renameBusy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
